/**
 * API Client Types and Interfaces
 * Core type definitions for the API client layer
 */

/**
 * HTTP Methods supported by the API
 */
export const HttpMethod = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
  HEAD: 'HEAD',
} as const;

export type HttpMethod = (typeof HttpMethod)[keyof typeof HttpMethod];

/**
 * API Request Configuration
 */
export interface ApiRequestConfig {
  method?: HttpMethod;
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  data?: unknown;
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  skipLog?: boolean;
  skipErrorHandling?: boolean;
}

/**
 * API Response Structure
 */
export interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  config: ApiRequestConfig;
}

/**
 * API Error Details
 */
export interface ApiErrorDetails {
  statusCode: number;
  message: string;
  error?: string;
  errors?: Record<string, string[]>;
  timestamp?: string;
  path?: string;
}

/**
 * Custom API Error Class
 */
export class ApiError extends Error {
  statusCode: number;
  override message: string;
  details?: ApiErrorDetails;
  response?: ApiResponse;

  constructor(
    statusCode: number,
    message: string,
    details?: ApiErrorDetails,
    response?: ApiResponse
  ) {
    super(message);
    this.statusCode = statusCode;
    this.message = message;
    this.details = details;
    this.response = response;
    this.name = 'ApiError';
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/**
 * Validation Error for field-specific errors
 */
export class ValidationError extends ApiError {
  fields: Record<string, string[]>;

  constructor(fields: Record<string, string[]>, details?: ApiErrorDetails) {
    super(400, 'Validation failed', details);
    this.fields = fields;
    this.name = 'ValidationError';
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

/**
 * Authentication Error for 401/403
 */
export class AuthenticationError extends ApiError {
  constructor(message = 'Authentication required', details?: ApiErrorDetails) {
    super(401, message, details);
    this.name = 'AuthenticationError';
    Object.setPrototypeOf(this, AuthenticationError.prototype);
  }
}

/**
 * Authorization Error for 403
 */
export class AuthorizationError extends ApiError {
  constructor(message = 'Access denied', details?: ApiErrorDetails) {
    super(403, message, details);
    this.name = 'AuthorizationError';
    Object.setPrototypeOf(this, AuthorizationError.prototype);
  }
}

/**
 * Not Found Error for 404
 */
export class NotFoundError extends ApiError {
  constructor(resource: string, details?: ApiErrorDetails) {
    super(404, `${resource} not found`, details);
    this.name = 'NotFoundError';
    Object.setPrototypeOf(this, NotFoundError.prototype);
  }
}

/**
 * Rate Limit Error for 429
 */
export class RateLimitError extends ApiError {
  retryAfter?: number;

  constructor(retryAfter?: number, details?: ApiErrorDetails) {
    super(429, 'Rate limit exceeded', details);
    this.retryAfter = retryAfter;
    this.name = 'RateLimitError';
    Object.setPrototypeOf(this, RateLimitError.prototype);
  }
}

/**
 * Network Error for connectivity issues
 */
export class NetworkError extends Error {
  message: string;
  originalError?: Error;

  constructor(message: string, originalError?: Error) {
    super(message);
    this.message = message;
    this.originalError = originalError;
    this.name = 'NetworkError';
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

/**
 * Interceptor types
 */
export interface RequestInterceptor {
  (config: ApiRequestConfig): ApiRequestConfig | Promise<ApiRequestConfig>;
}

export interface ResponseInterceptor<T = unknown> {
  (response: ApiResponse<T>): ApiResponse<T> | Promise<ApiResponse<T>>;
}

export interface ErrorInterceptor {
  (error: Error): Error | Promise<Error>;
}

/**
 * API Client Options
 */
export interface ApiClientOptions {
  baseUrl: string;
  timeout?: number;
  headers?: Record<string, string>;
  requestInterceptors?: RequestInterceptor[];
  responseInterceptors?: ResponseInterceptor[];
  errorInterceptors?: ErrorInterceptor[];
  enableLogging?: boolean;
  enableRetry?: boolean;
  defaultRetries?: number;
  defaultRetryDelay?: number;
}

/**
 * Type guard for ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Type guard for ValidationError
 */
export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

/**
 * Type guard for AuthenticationError
 */
export function isAuthenticationError(error: unknown): error is AuthenticationError {
  return error instanceof AuthenticationError;
}

/**
 * Type guard for AuthorizationError
 */
export function isAuthorizationError(error: unknown): error is AuthorizationError {
  return error instanceof AuthorizationError;
}

/**
 * Type guard for NotFoundError
 */
export function isNotFoundError(error: unknown): error is NotFoundError {
  return error instanceof NotFoundError;
}

/**
 * Type guard for RateLimitError
 */
export function isRateLimitError(error: unknown): error is RateLimitError {
  return error instanceof RateLimitError;
}

/**
 * Type guard for NetworkError
 */
export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}
